import { BaseMode } from '../BaseMode.js';
import { sessionManager } from '../../core/SessionManager.js';

/**
 * Intake Mode — the onboarding "what brings you here" conversation.
 *
 * Runs as a Foundation prerequisite after life-facts and relationship
 * screening. The agent reflects the screening, goes deep on one concern,
 * then `closeIntake` triggers the paths-forward handoff
 * (Continue Discovery / Start a conversation).
 *
 * Historical note: until 2026-07 intake extracted a TherapeuticIssue and
 * had an `add` entry type launched from the Focus Areas page — both retired
 * with the focus-areas feature.
 */
export class IntakeMode extends BaseMode {
  constructor() {
    super({
      name: 'INTAKE',
      contextBuilder: null
    });
    this.entryType = null;
    this.returnTo = null;
    this.pathId = null;
    // Snapshot of DiscoveryMode's left-block payload captured at switch time.
    // Renders the Foundation path name, its remaining-work control, and its
    // progress strip alongside intake's phase-label middle row, so none of them
    // flicker away for the duration of the prereq. Null for any non-Foundation
    // entry.
    this.inheritedLeftBlock = null;
    // Companion snapshot for the header's progress popover, captured at the
    // same moment as inheritedLeftBlock. Kept separate because the left block
    // is a render contract — see DiscoveryMode.getPathContextSnapshot. Null for
    // any non-Foundation entry, which keeps the popover inert there.
    this.inheritedPathContext = null;
  }

  /**
   * Lifecycle: enter intake mode.
   * Either resumes an existing INTAKE session (within 12h window) or starts a fresh one.
   *
   * @param {BaseMode|null} previousMode
   * @param {Object} transitionData
   * @param {string} [transitionData.entryType='onboarding']
   * @param {string} [transitionData.returnTo='DISCOVERY']
   * @param {string} [transitionData.pathId='foundation']
   * @param {string|null} [transitionData.resumeSessionId]
   */
  async onEnter(previousMode, transitionData = {}) {
    await super.onEnter(previousMode, transitionData);

    this.entryType = transitionData.entryType || 'onboarding';
    this.returnTo = transitionData.returnTo || 'DISCOVERY';
    this.pathId = transitionData.pathId || 'foundation';
    this.inheritedLeftBlock = transitionData.inheritedLeftBlock || null;
    // Copied at capture, not at read: the context is frozen for the duration of
    // the prereq, so a later DiscoveryMode mutation must not reach through it.
    this.inheritedPathContext = transitionData.inheritedPathContext
      ? { ...transitionData.inheritedPathContext }
      : null;

    // Resume vs. fresh:
    //   - Resume: ModeOrchestrator.switchMode() detected resumeSessionId and SKIPPED
    //     startSession entirely. We are responsible for attaching to the existing
    //     server session here.
    //   - Fresh: switchMode already called sessionManager.startSession('INTAKE') — do
    //     NOT create another session here.
    if (transitionData.resumeSessionId) {
      console.log('[IntakeMode] Resuming server-side session', transitionData.resumeSessionId);
      await sessionManager.attachToSession(transitionData.resumeSessionId, 'INTAKE');
    }
  }

  /**
   * Lifecycle: exit intake mode.
   *
   * Drops the inherited snapshots. This mode is a page-lifetime singleton, and
   * switchMode reassigns orchestrator.currentMode *before* awaiting
   * startSession and onEnter — so without this clear, a second intake entry
   * would expose the previous entry's context to anything reading the current
   * mode during that window (the still-painted discovery trigger is clickable
   * throughout it).
   */
  async onExit(nextMode, transitionData = {}) {
    this.inheritedLeftBlock = null;
    this.inheritedPathContext = null;
    await super.onExit(nextMode, transitionData);
  }

  /**
   * Path context for the header's progress popover, frozen at switch time.
   *
   * Returns a copy for the same reason getHeaderContentStrategy spreads
   * inheritedLeftBlock: the snapshot is frozen for intake's duration, so a
   * caller mutating the result must not reach back into stored state.
   *
   * The synthetic row is what gives the panel a "you are here" during intake.
   * Nothing in the path's questionnaire list is current — both prereq
   * questionnaires are already answered and the five sections have not started
   * — because the thing the user is actually doing is this conversation, which
   * is not a questionnaire and appears nowhere in the API payload.
   *
   * @returns {Object|null} Path context, or null for a non-Foundation entry
   */
  getPathContextSnapshot() {
    if (!this.inheritedPathContext) return null;

    return {
      ...this.inheritedPathContext,
      syntheticCurrentRow: { name: 'Your first conversation', status: 'in_progress' }
    };
  }

  /**
   * All user messages route to the LLM.
   */
  getRoutingStrategy(_message, _context) {
    return 'llm';
  }

  /**
   * Filter history: keep all INTAKE messages only.
   */
  filterHistory(allMessages) {
    return allMessages.filter(msg => !msg.mode || msg.mode === 'INTAKE');
  }

  getModeLabel() {
    return 'Intake Session';
  }

  /**
   * Phase-aware header content strategy. State lives strictly in the closure
   * so each re-activation starts fresh (re-created by HeaderContentService).
   */
  getHeaderContentStrategy() {
    let awaitingFirstMessage = true;
    let phase = 'identifying';

    // Snapshot taken before the mode switch — null for any non-Foundation
    // entry where there's no discovery header to inherit. Copied, not aliased:
    // it is frozen for the duration of intake, so a later discovery mutation
    // must not reach through this reference.
    const inheritedLeftBlock = this.inheritedLeftBlock
      ? { ...this.inheritedLeftBlock }
      : null;

    return {
      getUpdateTriggers: () => [
        'message:send',
        'state:update',
        'stream:failed',
        'action:button-click',
        'action:llm-triggered'
      ],

      shouldUpdate: (event, eventData) => {
        // `introducing_discovery` is the terminal phase — once the paths-
        // forward handoff body is showing, no further updates.
        if (phase === 'introducing_discovery') {
          return false;
        }

        if (event === 'message:send' && awaitingFirstMessage) {
          awaitingFirstMessage = false;
          phase = 'discussing';
          return true;
        }

        if (event === 'stream:failed' && phase === 'discussing' && !awaitingFirstMessage) {
          awaitingFirstMessage = true;
          phase = 'identifying';
          return true;
        }

        // close_intake fires after the agent's acknowledgment turn — the
        // moment the paths-forward handoff begins streaming.
        if (event === 'action:llm-triggered' && eventData?.type === 'close_intake') {
          phase = 'introducing_discovery';
          return true;
        }

        return false;
      },

      generateContent: () => {
        let label;
        if (phase === 'introducing_discovery') {
          label = 'Paths forward';
        } else if (phase === 'discussing') {
          label = 'Discussing your concern';
        } else {
          label = 'What brings you here';
        }

        return {
          leftBlock: inheritedLeftBlock || { title: this.getModeLabel() },
          middleBlock: { html: label }
        };
      }
    };
  }
}
